import { CommentVisibility } from '@policyfly/protobuf'
import { FeedItemVisibility } from '@policyfly/protobuf/pfactivity'
import {
  SchemaTranslations_FeedEventLink_RouteName,
} from '@policyfly/protobuf/pfschema'

import routeNames from '@/router/routeNames'

import type { ActivityFeedItem } from '@policyfly/protobuf/pfactivity'
import type { FeedEventLog } from 'types/events'
import type { RouteNamesValues } from 'types/router'
import type { UnifiedMembershipUser } from 'types/user'

/**
 * Converts Protobuf {@link ActivityFeedItem} to equivalent Django {@link FeedEventLog}.
 */
export function activityFeedItemToFeedEventLog (item: ActivityFeedItem): FeedEventLog | null {
  const created = new Date(item.created ? +item.created.seconds * 1000 : 0)
  const sharedFields: Pick<FeedEventLog, 'application_src' | 'created' | 'policy_src' | 'visibility'> = {
    application_src: '',
    created: item.created ? created.toISOString() : '',
    policy_src: 0,
    visibility: item.visibility,
  }

  switch (item.item.oneofKind) {
    case 'comment': {
      const comment = item.item.comment
      const attachmentNames = comment.attachments.length
        ? comment.attachments.map((a) => a.metadata?.name || '')?.join(' & ')
        : null
      const action: Pick<FeedEventLog, 'verb' | 'description' | 'data'> = comment.attachments.length
        ? {
            verb: 'uploaded',
            description: { text: attachmentNames! },
            data: { original_name: attachmentNames, policy_document: false },
          }
        : {
            verb: 'commented',
            data: {},
          }
      return {
        ...sharedFields,
        id: item.id as unknown as number, // allow uuids
        application: item.applicationId ? +item.applicationId : undefined,
        comment: {
          read_by: comment.readBy
            ? {
                id: comment.readBy.legacyId || 0,
                email: comment.readBy.email || '',
                first_name: comment.readBy.firstName || '',
                last_name: comment.readBy.lastName || '',
                avatar_url: '',
                is_active: false,
                active_at: '',
                isClerk: false,
                bingoId: comment.readBy.id || undefined,
              } as UnifiedMembershipUser
            : undefined,
          id: item.id as unknown as number, // allow uuids
          creator_role: '',
          restricted_visibility: [FeedItemVisibility.FEED_ITEM_VISIBILITY_RESTRICTED, FeedItemVisibility.FEED_ITEM_VISIBILITY_STAFF].includes(item.visibility),
          body: comment.content,
        },
        created_by: {
          id: 0,
          first_name: comment.author?.firstName ?? '',
          last_name: comment.author?.lastName ?? '',
        },
        policy: item.policyId ? +item.policyId : undefined,
        ...action,
        readOnly: comment.readable === false,
      }
    }
    case 'event': {
      const event = item.item.event
      const name = event.userName.split(' ')
      return {
        ...sharedFields,
        id: item.id as unknown as number, // allow uuids
        application: 0,
        comment: null,
        created_by: {
          id: 0,
          first_name: name[0],
          last_name: name.length > 1 ? name[1] : '',
        },
        policy: 0,
        verb: event.data?.data.oneofKind || '',
        data: event.data?.data || {},
      }
    }
  }

  return null
}

export function feedEventRouteNameToPolicyRouteName (routeName: SchemaTranslations_FeedEventLink_RouteName): RouteNamesValues | undefined {
  return feedEventRouteNameToPolicyRouteNameMap.get(routeName) ?? undefined
}

export function feedEventRouteNameToApplicationRouteName (routeName: SchemaTranslations_FeedEventLink_RouteName): RouteNamesValues | undefined {
  return feedEventRouteNameToApplicationRouteNameMap.get(routeName) ?? undefined
}

const feedEventRouteNameToPolicyRouteNameMap = new Map<SchemaTranslations_FeedEventLink_RouteName, RouteNamesValues | undefined>([
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_UNSPECIFIED, undefined],
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_ACTIVITY, routeNames.POLICY_FEED],
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_APPLICATION_REVIEW, routeNames.POLICY_REVIEW],
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_ATTACHMENTS, routeNames.POLICY_ATTACHMENTS],
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_POLICY_DOCS, routeNames.POLICY_POLICY_DOCS],
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_QUOTE_REVIEW, undefined],
])

const feedEventRouteNameToApplicationRouteNameMap = new Map<SchemaTranslations_FeedEventLink_RouteName, RouteNamesValues | undefined>([
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_UNSPECIFIED, undefined],
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_ACTIVITY, routeNames.APPLICATION_FEED],
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_APPLICATION_REVIEW, routeNames.APPLICATION_REVIEW],
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_ATTACHMENTS, routeNames.APPLICATION_ATTACHMENTS],
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_POLICY_DOCS, routeNames.APPLICATION_POLICY_DOCS],
  [SchemaTranslations_FeedEventLink_RouteName.ROUTE_NAME_QUOTE_REVIEW, routeNames.APPLICATION_REVIEW_QUOTES],
])

export function commentVisibilityToFeedItemVisibility (visibility: CommentVisibility | undefined): FeedItemVisibility | undefined {
  switch (visibility) {
    case CommentVisibility.COMMENT_VISIBILITY_RESTRICTED: return FeedItemVisibility.FEED_ITEM_VISIBILITY_RESTRICTED
    case CommentVisibility.COMMENT_VISIBILITY_STAFF: return FeedItemVisibility.FEED_ITEM_VISIBILITY_STAFF
    case CommentVisibility.COMMENT_VISIBILITY_STANDARD: return FeedItemVisibility.FEED_ITEM_VISIBILITY_STANDARD
    default: return undefined
  }
}
